<!-- src/views/PerformerPreview.vue -->
<template>
  <div class="container-fluid px-3 py-3">
    <!-- Top bar -->
    <div class="d-flex align-items-center mb-3">
      <h5 class="m-0">Performer Preview</h5>
    </div>

    <!-- Card: Header -->
    <div class="card shadow-sm mb-3">
      <div class="card-body text-center">
        <!-- Photo -->
        <div v-if="imgSrc" class="mb-3">
          <img :src="imgSrc" alt="Performer photo" class="rounded" style="width: 140px; height: 140px; object-fit: cover;">
        </div>

        <!-- Name -->
        <h4 v-if="performer.name" class="mb-1">{{ performer.name }}</h4>

        <!-- Type -->
        <div v-if="performer.performer_type" class="text-muted">
          {{ performer.performer_type }}
        </div>
      </div>
    </div>

    <!-- About -->
    <div v-if="performer.about" class="card shadow-sm mb-3">
      <div class="card-header fw-semibold">About</div>
      <div class="card-body">
        <p class="mb-0">{{ performer.about }}</p>
      </div>
    </div>

    <!-- Socials -->
    <div v-if="hasAnySocial" class="card shadow-sm mb-3">
      <div class="card-header fw-semibold">Social</div>
      <div class="list-group list-group-flush">
        <a
          v-if="performer.instagram"
          class="list-group-item list-group-item-action d-flex align-items-center"
          :href="buildInstagramUrl(performer.instagram)"
          target="_blank" rel="noopener"
        >
          <span class="input-group-text me-2"><i class="ph-bold ph-instagram-logo"></i></span>
          <span class="flex-grow-1">@{{ trimAt(performer.instagram) }}</span>
          <i class="ph ph-arrow-up-right ms-2"></i>
        </a>

        <a
          v-if="performer.facebook"
          class="list-group-item list-group-item-action d-flex align-items-center"
          :href="buildFacebookUrl(performer.facebook)"
          target="_blank" rel="noopener"
        >
          <span class="input-group-text me-2"><i class="ph-bold ph-facebook-logo"></i></span>
          <span class="flex-grow-1">{{ compactFacebook(performer.facebook) }}</span>
          <i class="ph ph-arrow-up-right ms-2"></i>
        </a>

        <a
          v-if="performer.tiktok"
          class="list-group-item list-group-item-action d-flex align-items-center"
          :href="buildTiktokUrl(performer.tiktok)"
          target="_blank" rel="noopener"
        >
          <span class="input-group-text me-2"><i class="ph-bold ph-tiktok-logo"></i></span>
          <span class="flex-grow-1">@{{ trimAt(performer.tiktok) }}</span>
          <i class="ph ph-arrow-up-right ms-2"></i>
        </a>
         <a
          v-if="performer.dreamdj"
          class="list-group-item list-group-item-action d-flex align-items-center"
          :href="performer.dreamdj"
          target="_blank" rel="noopener"
        >
          <span class="input-group-text me-2"><i class="ph-bold ph-tiktok-logo"></i></span>
          <span class="flex-grow-1">@{{ extractDreamDJArtist(performer.dreamdj) }}</span>
          <i class="ph ph-arrow-up-right ms-2"></i>
        </a>
      </div>
    </div>

    <!-- Tips -->
    <div v-if="hasAnyTips" class="card shadow-sm mb-4">
      <div class="card-header fw-semibold">Tip</div>
      <div class="list-group list-group-flush">
        <!-- Cash App -->
        <a
          v-if="performer.cashapp"
          class="list-group-item list-group-item-action d-flex align-items-center"
          :href="buildCashAppUrl(performer.cashapp)"
          target="_blank" rel="noopener"
        >
          <span class="input-group-text me-2 fw-bold">$</span>
          <span class="flex-grow-1">${{ trimDollar(performer.cashapp) }}</span>
          <i class="ph ph-arrow-up-right ms-2"></i>
        </a>

        <!-- Venmo -->
        <a
          v-if="performer.venmo"
          class="list-group-item list-group-item-action d-flex align-items-center"
          :href="buildVenmoUrl(performer.venmo)"
          target="_blank" rel="noopener"
        >
          <span class="input-group-text me-2 fw-bold">V</span>
          <span class="flex-grow-1">@{{ trimAt(performer.venmo) }}</span>
          <i class="ph ph-arrow-up-right ms-2"></i>
        </a>

        <!-- PayPal (email) -->
        <div
          v-if="performer.paypal"
          class="list-group-item list-group-item-action d-flex align-items-center"
          :href="`mailto:${performer.paypal}`"
        >
          <span class="input-group-text me-2"><i class="ph-bold ph-paypal-logo"></i></span>
          <span class="flex-grow-1">{{ performer.paypal }}</span>
          <i class="ph ph-envelope-simple ms-2"></i>
        </div>
      </div>
    </div>

    <!-- Action buttons -->
    <div class="d-grid gap-2">
      <button class="btn btn-dark btn-lg" @click="confirmAndReturn">
        Looks good — Save this
      </button>
      <button class="btn btn-outline-dark btn-lg" @click="goBack()">
        Edit details
      </button>
    </div>
  </div>
</template>

<script>
import performerService from "../../api/performer.js"
import { takeFile } from '../../store/tempFiles';

export default {
  name: 'PerformerPreview',
  props: {
    performer: { type: Object, required: true }
  },
  computed: {
    imgSrc() {
      // allow dataURL preview OR external URL
      return this.performer.photoPreview || '';
    },
    hasAnySocial() {
      const p = this.performer || {};
      return !!(p.instagram || p.facebook || p.tiktok);
    },
    hasAnyTips() {
      const p = this.performer || {};
      return !!(p.cashapp || p.venmo || p.paypal);
    }
  },
  methods: {
    extractDreamDJArtist(s = '') {
    const m = s.match(/\/([^/?#]+)(?:[?#].*)?$/);
    return m ? decodeURIComponent(m[1]) : s;
    },
    goBack(){
        this.$router.push({
      name: 'PerformerForm',
      query: { data: JSON.stringify(this.performer) }
    });
    },
    // helpers for display
    trimAt(v = '') {
      return v.replace(/^@+/, '');
    },
    trimDollar(v = '') {
      return v.replace(/^\$+/, '');
    },
    compactFacebook(v = '') {
    // Grab only the slug right after facebook.com/
    const m = v.match(/facebook\.com\/([^/?#]+)/i);
    return m ? m[1] : v;
    },
    // URL builders
    buildInstagramUrl(v = '') {
      const h = this.trimAt(v);
      return `https://instagram.com/${h}`;
    },
    buildFacebookUrl(v = '') {
      if (/facebook\.com\//i.test(v)) return v;
      return `https://facebook.com/${v}`;
    },
    buildTiktokUrl(v = '') {
      const h = this.trimAt(v);
      return `https://www.tiktok.com/@${h}`;
    },
    buildDreamDjUrl(v = '') {
      const h = this.trimAt(v);
      return `https://www.tiktok.com/@${h}`;
    },
    buildCashAppUrl(v = '') {
      const c = this.trimDollar(v);
      return `https://cash.app/$${c}`;
    },
    buildVenmoUrl(v = '') {
      const h = this.trimAt(v);
      return `https://venmo.com/u/${h}`;
    },

    // Example “save” action: emit to parent or navigate
    async confirmAndReturn() {
       var payload = ""

       if(this.performer.fileID){
        const image = takeFile(this.performer.fileID)
        const data = await performerService.getSignPerformerS3URL(image.type)
        await fetch(data.url, {
        method: "PUT",
        headers: {
          "Content-Type": image.type
        },
        body: image
      })

      const profile_picture = data.url.split('?')[0]
      payload = { ...this.performer, profile_picture };
       delete payload.pictureObj;
       delete payload.photoPreview;
       }
       else{
        payload = { ...this.performer };
       }


    

   
      
      performerService.saveNewPerformer(payload).then(() => {
        this.$router.push({name: 'Performer-Done'});
        })

      // Optionally, you could use an event bus or a shared store to kick off save.
    }
  }
}
</script>

<style scoped>
.card-header { background: #fafafa; }
.input-group-text {
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.list-group-item .input-group-text {
  border: none;
  background: transparent;
  padding: 0;
}
</style>
